import { ReactNode, useCallback } from 'react';
import { useGlobalPanel } from '@frontend/panel-engine';
import { MessageInsightsAccordionType, MessageInsightsPanelSource, PanelEvents } from '../types';
import { MessageData, useMessageInsightsPanelShallowStore } from './use-message-insights-panel-store';
import { usePanelEmitter } from './use-panel-emitter';

export type OpenParams = {
  messageId: string;
  threadId: string;
  locationId: string;
  createdAt?: string;
  escalationSubject?: string;
  escalationBody?: string;
  outcomeBubbleNode?: ReactNode;
};

export type OpenOptions = {
  defaultAccordion?: MessageInsightsAccordionType;
  isDemoAccount?: boolean;
};

type UseMessageInsightsControllerProps = {
  source: MessageInsightsPanelSource;
};

export interface MessageInsightsApi {
  activeMessage: MessageData | null;
  isLoading: boolean;
  isOpen: boolean;
  emitEvent: <K extends keyof PanelEvents>(event: K, payload?: PanelEvents[K]) => void;
  open: (params: OpenParams, opts?: OpenOptions) => void;
  offEvent: <K extends keyof PanelEvents>(event: K, handler: (payload: PanelEvents[K]) => void) => void;
  onEvent: <K extends keyof PanelEvents>(event: K, handler: (payload: PanelEvents[K]) => void) => void;
}

export const useMessageInsightsController = ({ source }: UseMessageInsightsControllerProps): MessageInsightsApi => {
  const { open: openGlobal, isOpen } = useGlobalPanel('message-insights');

  const emitter = usePanelEmitter(source);

  const { selectedMessage: activeMessage, openContext } = useMessageInsightsPanelShallowStore(
    'selectedMessage',
    'openContext'
  );

  const open = useCallback(
    (params: OpenParams, opts?: OpenOptions) => {
      const messageData: MessageData = {
        messageId: params.messageId,
        threadId: params.threadId,
        locationId: params.locationId,
        createdAt: params.createdAt,
        escalationSubject: params.escalationSubject,
        escalationBody: params.escalationBody,
        outcomeBubbleNode: params.outcomeBubbleNode,
      };

      openContext({ message: messageData, opts });
      openGlobal();
    },
    [openContext, openGlobal]
  );

  const onEvent = useCallback(
    <K extends keyof PanelEvents>(event: K, handler: (payload: PanelEvents[K]) => void) => {
      emitter.on(event, handler);
    },
    [emitter]
  );

  const offEvent = useCallback(
    <K extends keyof PanelEvents>(event: K, handler: (payload: PanelEvents[K]) => void) => {
      emitter.off(event, handler);
    },
    [emitter]
  );

  const emitEvent = useCallback(
    <K extends keyof PanelEvents>(event: K, payload?: PanelEvents[K]) => {
      emitter.emit(event, payload as PanelEvents[K]);
    },
    [emitter]
  );

  return {
    activeMessage,
    isLoading: false,
    isOpen,
    emitEvent,
    open,
    offEvent,
    onEvent,
  };
};
